import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import configService from '../services/config';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
} from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';
import OfflinePinIcon from '@mui/icons-material/OfflinePin';

export default function Approve_Regularization() {
  const navigate = useNavigate();
  const theme = useTheme();

  // --- Breakpoints ---
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:420px)');

  const {
    company_id,
    userId,
    permissions = [],
  } = useSelector((state) => state.globalVariables);

  const REQUIRED_PERMISSION = 86;
  const hasPermission = permissions?.includes(REQUIRED_PERMISSION);

  const [regularizations, setRegularizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const customerId = userId || 8;
      const response = await configService.getSubordinateCustomerRegularizationList({
        customer_id: customerId,
      });
      const data = response?.data ?? response;
      if (data?.success === true) {
        const list = Array.isArray(data.regularizations) ? data.regularizations : [];
        setRegularizations(list);
      } else {
        setRegularizations([]);
        toast.error(data?.message || 'No regularization data found');
      }
    } catch (error) {
      console.error('Subordinate regularization fetch error:', error);
      setRegularizations([]);
      toast.error(error?.response?.data?.message || 'Failed to load regularization data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasPermission]);

  const handleOpen = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleApproveOrReject = async (row) => {
    const result = await Swal.fire({
      title: 'Update Regularization Status',
      text: 'Please select an action to proceed:',
      icon: 'question',
      input: 'select',
      inputOptions: {
        Approved: 'Approve',
        Denied: 'Reject',
      },
      inputPlaceholder: 'Select Status',
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Close',
      confirmButtonColor: '#007bff',
      inputValidator: (value) => {
        if (!value) {
          return 'You need to select a status!';
        }
      },
    });

    if (!result.isConfirmed || !result.value) return;

    try {
      const loggedInCustomerId = userId || 8;
      const response = await configService.updateRegularizationStatus(row.id, {
        status: result.value,
        customer_id: loggedInCustomerId,
      });

      if (response.status === 200) {
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Regularization ${result.value} successfully.`,
        });
        setRegularizations((prev) =>
          prev.map((item) => (item.id === row.id ? { ...item, status: result.value } : item))
        );
      }
    } catch (error) {
      console.error('Status update error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong!',
      });
    }
  };

  const fmtDate = (date) => {
    if (!date) return '—';
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return '—';
    return parsedDate.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const fmtTime = (time) => {
    if (!time || time === '00:00:00') return '—';
    return time;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
      case 'Denied':
        return 'error';
      case 'Pending':
      default:
        return 'warning';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- Responsive values ---
  const rootPadding = isSmallMobile ? 1 : isMobile ? 1.5 : 3;
  const titleFontSize = isSmallMobile ? '1.1rem' : isMobile ? '1.25rem' : '1.5rem';
  const tableMinWidth = isSmallMobile ? 600 : isMobile ? 700 : 700;
  const dialogFullScreen = isMobile;

  // --- 403 Forbidden (unchanged, but responsive) ---
  if (!hasPermission) {
    return (
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          px: 2,
        }}
      >
        <Box
          sx={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            padding: { xs: '32px 24px', md: '40px 48px' },
            maxWidth: '560px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              flexWrap: 'wrap',
              mb: 3.5,
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: { xs: '52px', md: '64px' },
                  fontWeight: 800,
                  lineHeight: 1,
                  color: '#f0576b',
                }}
              >
                403
              </Typography>
              <Typography
                sx={{
                  fontSize: '26px',
                  fontWeight: 700,
                  color: '#f5a623',
                  mt: '6px',
                  mb: '14px',
                }}
              >
                Forbidden
              </Typography>
              <Typography
                sx={{
                  color: '#f0576b',
                  fontSize: '13px',
                  lineHeight: 1.5,
                }}
              >
                Sorry, you're not allowed in here
                <br />
                Please contact IT
              </Typography>
            </Box>

            <svg width="150" height="150" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.9">
                <circle cx="150" cy="42" r="20" fill="#e9e9e9" stroke="#cfcfcf" strokeWidth="1.5" />
                <path d="M130 42h40M150 22v40M136 28l28 28M164 28l-28 28" stroke="#cfcfcf" strokeWidth="1" />
                <path d="M120 20l3 6 6 1-4.5 4.5 1 6-5.5-3-5.5 3 1-6L112 26l6-1z" fill="#ffd166" />
                <path d="M178 60l2 4 4 1-3 3 .5 4-3.5-2-3.5 2 .5-4-3-3 4-1z" fill="#ffd166" />
              </g>
              <ellipse cx="90" cy="150" rx="34" ry="6" fill="#000" opacity="0.06" />
              <rect x="70" y="86" width="12" height="66" rx="5" fill="#111" />
              <rect x="108" y="86" width="12" height="66" rx="5" fill="#111" />
              <rect x="55" y="90" width="80" height="66" rx="16" fill="#1a1a1a" />
              <rect x="80" y="96" width="30" height="44" rx="10" fill="#262626" />
              <circle cx="90" cy="60" r="22" fill="#e0ac69" />
              <path d="M68 58a22 22 0 0 1 44 0v-4a22 22 0 0 0-44 0z" fill="#3b2a20" />
              <rect x="72" y="56" width="36" height="10" rx="5" fill="#111" />
              <circle cx="35" cy="86" r="6" fill="#111" />
              <circle cx="45" cy="150" r="6" fill="#111" />
              <path d="M35 86c-4 20 5 45 10 64" stroke="#d4a24c" strokeWidth="2.5" fill="none" />
              <path d="M45 150c40 10 90 10 130 0" stroke="#c8102e" strokeWidth="4" fill="none" />
              <circle cx="175" cy="150" r="6" fill="#111" />
            </svg>
          </Box>

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#333', mb: 1 }}>
            403 - Not Authorized
          </Typography>

          <Typography variant="body1" sx={{ color: '#777' }}>
            You do not have permission to access this page.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate('/dashboard/dashboard')}
            sx={{ mt: 3, px: 4, py: 1, textTransform: 'none', fontWeight: 600 }}
          >
            Go to Home
          </Button>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const paginatedRows = regularizations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // --- Render Cards for mobile/tablet ---
  const renderCards = () => (
    <Stack spacing={2} sx={{ width: '100%', py: 1 }}>
      {paginatedRows.map((row) => {
        const status = row.status || 'Pending';
        const statusColor = getStatusColor(status);
        return (
          <Card key={row.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: 'none' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {row.name || '—'}
                  </Typography>
                  <Chip label={status.toUpperCase()} color={statusColor} size="small" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Category:</span> {row.category || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Time Type:</span> {row.time_type || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Entry Date:</span> {fmtDate(row.entry_date)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <IconButton onClick={() => handleOpen(row)} size="small" sx={{ color: '#6c5ce7' }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton onClick={() => handleApproveOrReject(row)} color="secondary" size="small">
                    <OfflinePinIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  // --- Render Table for desktop ---
  const renderTable = () => (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, width: '100%', overflowX: 'auto' }}>
      <Table sx={{ width: '100%', minWidth: tableMinWidth }}>
        <TableHead sx={{ backgroundColor: '#1976d2' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Time Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Entry Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.map((row) => {
            const status = row.status || 'Pending';
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {row.name || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{row.category || '—'}</TableCell>
                <TableCell>{row.time_type || '—'}</TableCell>
                <TableCell>{fmtDate(row.entry_date)}</TableCell>
                <TableCell>
                  <Chip label={status.toUpperCase()} color={getStatusColor(status)} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(row)}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton onClick={() => handleApproveOrReject(row)} color="secondary">
                    <OfflinePinIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        p: rootPadding,
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          sx={{ color: '#1976d2', fontSize: titleFontSize }}
        >
          Attendance Regularization
        </Typography>
      </Box>

      {regularizations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
          <Typography color="text.secondary">
            No regularization requests found for your subordinates.
          </Typography>
        </Paper>
      ) : (
        <>
          {isMobileOrTablet ? renderCards() : renderTable()}

          <TablePagination
            component="div"
            count={regularizations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-end',
              },
            }}
          />
        </>
      )}

      {/* ── Details Dialog ── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={dialogFullScreen}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            p: isMobile ? 1 : 0,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1e293b' }}>
          Regularization Details
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#e2e8f0' }}>
          {selectedRow && (
            <Stack spacing={1.5}>
              <Typography><b>Employee:</b> {selectedRow.name || '-'}</Typography>
              <Typography><b>Customer ID:</b> {selectedRow.customer_id || '-'}</Typography>
              <Typography><b>Category:</b> {selectedRow.category || '-'}</Typography>
              <Typography><b>Time Type:</b> {selectedRow.time_type || '-'}</Typography>
              <Typography><b>Entry Date:</b> {fmtDate(selectedRow.entry_date)}</Typography>
              <Typography><b>Beginning:</b> {fmtTime(selectedRow.beginning)}</Typography>
              <Typography><b>End Time:</b> {fmtTime(selectedRow.end_time)}</Typography>
              <Typography><b>Remarks:</b> {selectedRow.remarks || '-'}</Typography>
              <Typography><b>Status:</b> {selectedRow.status || '-'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}